import type { CreditsStatus } from '../types/db';

/**
 * Badge crediti + cap testate per il client loggato.
 * Sopra il contenuto della dashboard, render una banda informativa con
 * 2 chip colorati. Se subscription_status != 'active' aggiunge un banner
 * rosso sopra.
 */
export default function CreditsBar({ credits }: { credits: CreditsStatus | null }) {
  if (!credits) return null;

  const usedPct =
    credits.monthly_credits_quota > 0
      ? Math.min(100, (Number(credits.credits_used) / Number(credits.monthly_credits_quota)) * 100)
      : 0;

  // Colori secondo soglie di consumo
  const creditColor =
    usedPct >= 100
      ? 'bg-rose-100 text-rose-800 ring-rose-300'
      : usedPct >= 80
        ? 'bg-amber-100 text-amber-800 ring-amber-300'
        : 'bg-emerald-100 text-emerald-800 ring-emerald-300';

  const productionsAtCap = credits.active_productions_count >= credits.max_active_productions;
  const productionsColor = productionsAtCap
    ? 'bg-amber-100 text-amber-800 ring-amber-300'
    : 'bg-slate-100 text-slate-700 ring-slate-200';

  const renewalDate = credits.current_period_end
    ? new Date(credits.current_period_end).toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
      })
    : null;

  const subBanner =
    credits.subscription_status !== 'active' ? (
      <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        <strong>Sottoscrizione non attiva</strong> (
        <span className="font-mono">{credits.subscription_status}</span>). Le generazioni sono
        bloccate. Contatta il supporto per riattivare il piano.
      </div>
    ) : null;

  return (
    <div className="mb-6 space-y-3">
      {subBanner}
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${creditColor}`}
          title={
            renewalDate
              ? `Rinnovo previsto: ${renewalDate}${credits.last_payment_at ? ` · Ultimo pagamento: ${new Date(credits.last_payment_at).toLocaleDateString('it-IT')}` : ''}`
              : undefined
          }
        >
          Crediti: {Number(credits.credits_used).toFixed(1)} /{' '}
          {Number(credits.monthly_credits_quota).toFixed(0)}
          {credits.is_blocked && <span className="ml-1">· esauriti</span>}
        </span>

        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${productionsColor}`}
          title={productionsAtCap ? 'Cap del piano raggiunto. Archivia o fai upgrade.' : undefined}
        >
          Testate: {credits.active_productions_count} / {credits.max_active_productions}
          {productionsAtCap && <span className="ml-1">· cap raggiunto</span>}
        </span>

        <span className="text-xs uppercase tracking-wide text-slate-400">
          Piano {credits.plan_tier}
        </span>
      </div>
    </div>
  );
}
